import { useState } from 'react'
import { IconCloseButton } from '@/components/Button/IconCloseButton'
import { Body1, Caption1, Heading4, Title1, Title2 } from '@/components/ui/typography'
import { validateLayout } from '@/routes/Dashboard/utils'
import { Input } from 'components/Input'

import { DashboardLayout, DESCRIPTION_LIMIT, LAYOUT_NAME_REGEX, TITLE_LIMIT } from '../types'

interface CreateLayoutModalProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (name: string, description: string, presetId?: string) => void
  presetLayouts: DashboardLayout[]
}

export const CreateLayoutModal: React.FC<CreateLayoutModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  presetLayouts,
}) => {
  const [layoutName, setLayoutName] = useState('')
  const [layoutDescription, setLayoutDescription] = useState('')
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const [error, setError] = useState('')

  const handleCreate = () => {
    const result = validateLayout({
      name: layoutName,
      description: layoutDescription,
      titleLimit: TITLE_LIMIT,
      descriptionLimit: DESCRIPTION_LIMIT,
      nameRegex: LAYOUT_NAME_REGEX,
    })

    if (!result.isValid) {
      setError(result.error!)
      return
    }

    onCreate(result.trimmedName, result.trimmedDescription, selectedPreset || undefined)

    setLayoutName('')
    setLayoutDescription('')
    setSelectedPreset(null)
    setError('')
    onClose()
  }
  const handleClose = () => {
    setLayoutName('')
    setLayoutDescription('')
    setSelectedPreset(null)
    setError('')
    onClose()
  }
  if (!isOpen) {
    return null
  }

  return (
    <>
      <div
        className='fixed inset-0 z-[1000] flex items-center justify-center bg-black/50'
        onClick={handleClose}
      />

      <div
        className='
        fixed left-1/2 top-1/2 z-[1001]
        w-[90%] max-w-[560px] max-h-[85vh]
        -translate-x-1/2 -translate-y-1/2
        flex flex-col
        rounded-xl bg-white shadow-2xl
      '
      >
        <div className='flex items-center justify-between border-b border-black/10 p-6'>
          <Heading4>Create New Layout</Heading4>

          <IconCloseButton onClick={handleClose} />
        </div>

        <div className='flex-1 overflow-y-auto p-6'>
          <div className='mb-6'>
            <Title2 className='mb-2 block font-medium text-gray-600 text-[var(--foreground)]'>
              Layout Name *
            </Title2>

            <Input
              type='text'
              value={layoutName}
              maxLength={TITLE_LIMIT}
              onChange={(e) => {
                setLayoutName(e.target.value)
                setError('')
              }}
            />

            <Caption1 className='text-xs text-gray-400 mt-1'>
              {layoutName.length}/{TITLE_LIMIT}
            </Caption1>

            {error && <Caption1 className='mt-1 text-sm text-red-500'>{error}</Caption1>}
          </div>

          <div className='mb-1'>
            <Title2 className='mb-2 block text-gray-600'>Description (optional)</Title2>

            <textarea
              value={layoutDescription}
              onChange={(e) => setLayoutDescription(e.target.value)}
              maxLength={DESCRIPTION_LIMIT}
              rows={3}
              placeholder='Describe the purpose of this layout...'
              className='
              w-full resize-y rounded-md border border-[var(--border)]
              bg-[var(--background)] p-2 text-sm
              transition-all focus:border-[var(--sidebar-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--sidebar-primary)]/20
            '
            />
          </div>
          <Caption1 className='text-xs text-gray-400 mb-4'>
            {layoutDescription.length}/{DESCRIPTION_LIMIT}
          </Caption1>

          <div className='mb-6'>
            <Title2 className='mb-2 font-medium text-gray-600'>
              Start from template (optional)
            </Title2>

            <div className='flex max-h-[20rem] flex-col gap-3 overflow-y-auto'>
              <div
                onClick={() => setSelectedPreset(null)}
                className={`
                flex cursor-pointer items-start gap-4 rounded-lg border-2 p-4 transition-all
                ${
                  selectedPreset === null
                    ? 'border-[var(--sidebar-primary)] bg-[var(--accent)]'
                    : 'border-[var(--border)] hover:border-[var(--sidebar-primary)] hover:bg-[var(--accent)]'
                }
              `}
              >
                <div className='text-3xl flex-shrink-0'>📄</div>

                <div className='flex flex-1 flex-col gap-1'>
                  <Title1 className='font-medium text-gray-600'>Blank Layout</Title1>

                  <Caption1 className='text-gray-500'>Start with an empty dashboard</Caption1>
                </div>
              </div>

              {presetLayouts.map((preset) => {
                const selected = selectedPreset === preset.id

                return (
                  <div
                    key={preset.id}
                    onClick={() => setSelectedPreset(preset.id)}
                    className={`
                    flex cursor-pointer items-start gap-4 rounded-lg border-2 p-4 transition-all
                    ${
                      selected
                        ? 'border-[var(--sidebar-primary)] bg-[var(--accent)]'
                        : 'border-[var(--border)] hover:border-[var(--sidebar-primary)] hover:bg-[var(--accent)]'
                    }
                  `}
                  >
                    <div className='text-3xl flex-shrink-0'>📊</div>

                    <div className='flex flex-1 flex-col gap-1'>
                      <Body1 className='leading-none text-gray-600'>{preset.name}</Body1>

                      <Caption1 className='text-gray-500'>{preset.description}</Caption1>

                      <Caption1 className='text-black'>{preset.widgets.length} widgets</Caption1>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className='flex justify-end gap-4 border-t border-[var(--border)] p-6'>
          <button
            onClick={handleClose}
            className='rounded-lg border border-[var(--border)] bg-white px-6 py-2.5 text-sm font-medium text-[var(--foreground)] transition-all hover:bg-[var(--accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ring)]'
          >
            Cancel
          </button>

          <button
            onClick={handleCreate}
            className='rounded-lg bg-[var(--sidebar-primary)] px-6 py-2.5 text-sm font-medium text-white transition-all hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ring)]'
          >
            Create Layout
          </button>
        </div>
      </div>
    </>
  )
}
